import { z } from 'zod';
/**
 * One error shape for every endpoint, following RFC 9457.
 *
 * The predecessor had zero of 95 routes declaring a schema, so a malformed
 * request reached the database and surfaced as a 500 with a Postgres type error
 * in it. A caller could not tell "you sent the wrong thing" from "we are
 * broken", and neither could an operator reading the logs.
 */
export const problemDetailsSchema = z.object({
    /** A stable identifier for the kind of failure, safe to branch on. */
    type: z.string(),
    /** Short, human-readable, and the same for every occurrence of this type. */
    title: z.string(),
    status: z.number().int().min(400).max(599),
    /** What went wrong *this* time. Never includes internals or SQL. */
    detail: z.string(),
    /** Field-level problems, when the failure is a validation failure. */
    errors: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});
/**
 * Turns a schema failure into a response body.
 *
 * Deliberately reports every field at once: a client fixing one field per
 * round-trip is the API equivalent of the five-deploys-five-outages problem.
 */
export function validationProblem(issues) {
    return {
        type: 'https://carbonweb.co/problems/validation',
        title: 'The request body did not match the expected shape',
        status: 400,
        detail: 'One or more fields are missing or invalid.',
        errors: issues.map((issue) => ({
            path: issue.path.join('.'),
            message: issue.message,
        })),
    };
}
function problem(type, title, status, detail) {
    return { type, title, status, detail };
}
export const notFound = (detail) => problem('https://carbonweb.co/problems/not-found', 'Not found', 404, detail);
/**
 * Understood, and refused because of who is asking.
 *
 * Distinct from `unauthorized`, which means "we do not know who you are". A
 * signed-in member who is not an admin is neither unknown nor mistaken, and
 * telling them to sign in would send them round a loop that cannot help.
 */
export const forbidden = (detail) => problem('https://carbonweb.co/problems/forbidden', 'Not allowed', 403, detail);
export const unauthorized = (detail) => problem('https://carbonweb.co/problems/unauthorized', 'Not authorised', 401, detail);
/**
 * The one message an operator should never learn something from and a caller
 * should never learn anything from. Internals stay in the logs.
 */
export const internalError = () => problem('https://carbonweb.co/problems/internal', 'Something went wrong', 500, 'The request could not be completed. It has been logged.');
/**
 * A request that was understood and refused for a reason the caller can act on.
 *
 * A wrong current password, a password identical to the old one, an account
 * with no password at all: each is an ordinary answer rather than a fault, and
 * the sentence the screen shows has to be the one the server chose. Left as a
 * bare `{ title }` the body does not parse as a problem document at all, and
 * every one of these would reach the person as the same generic line.
 */
export const refused = (status, detail) => problem('https://carbonweb.co/problems/refused', 'Refused', status, detail);
//# sourceMappingURL=problemDetails.js.map